import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/auth-store.js';
import Logo from '../components/Logo.js';

export default function AppShell() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);

  return (
    <div className="relative min-h-dvh-safe bg-ec-ground text-ec-bone">
      {/* Ambient stadium backdrop — floodlight glow + a low turf rise. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 [background:radial-gradient(ellipse_55%_45%_at_18%_-5%,rgba(245,168,28,0.09),transparent_65%),radial-gradient(ellipse_50%_40%_at_85%_0%,rgba(39,147,211,0.11),transparent_65%),radial-gradient(ellipse_at_50%_125%,rgba(85,167,47,0.08),transparent_60%)]"
      />
      {/* Edge vignette — pulls the focus to the centre, away from flat edges. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 [background:radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.55))]"
      />
      {/* Film grain over the whole app so no surface reads as a flat fill. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.035] mix-blend-overlay [background-image:var(--ec-grain-dark)] [background-size:160px_160px]"
      />

      <header className="relative border-b border-ec-line bg-ec-ground/80 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 sm:px-6 sm:py-4">
          <div>
            <Link to="/" className="flex items-center gap-3">
              <h1 className="m-0">
                <Logo surface="dark" />
              </h1>
              <p className="hidden self-end font-serif text-sm italic text-ec-bone-dim lg:block">
                Heritage footy, dealt by the crow.
              </p>
            </Link>
          </div>
          <nav className="flex flex-wrap items-center gap-2 font-display text-sm uppercase tracking-wide sm:gap-3">
            {user ? (
              <>
                <Link to="/lobbies" className="text-ec-bone transition-colors hover:text-ec-face">
                  Lobbies
                </Link>
                <Link to="/me/matches" className="text-ec-bone transition-colors hover:text-ec-face">
                  Your matches
                </Link>
                <Link to="/my-teams" className="text-ec-bone transition-colors hover:text-ec-face">
                  My Teams
                </Link>
                <Link to="/leaderboard" className="text-ec-bone transition-colors hover:text-ec-face">
                  Leaderboard
                </Link>
                <Link to="/me" className="text-ec-bone transition-colors hover:text-ec-face">
                  Your account
                </Link>
                <span className="hidden text-ec-bone-dim sm:inline">
                  Signed in as <span className="font-medium text-ec-brass">{user.displayName}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    clear();
                    navigate('/login');
                  }}
                  className="ec-btn !py-1 text-xs"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-ec-bone transition-colors hover:text-ec-face">
                  Log in
                </Link>
                <Link to="/register" className="ec-btn ec-btn--primary !py-1 text-xs">
                  Register
                </Link>
              </>
            )}
          </nav>
        </div>
        {/* Foil rule stamped under the masthead. */}
        <div aria-hidden className="absolute inset-x-0 -bottom-px h-px [background:var(--ec-foil-rule)] opacity-70" />
      </header>
      <main className="relative px-4 py-6 sm:px-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
